import SearchOrigin from '@alltrails/amplitude/enums/SearchOrigin';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { User } from '@alltrails/types/User';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import type { AlgoliaResultType, SearchType } from '@alltrails/types/Algolia';
import { getLanguageCodeFromLanguageRegionCode } from '@alltrails/core/utils/languageTypeModifiers';

// IMPORTANT: You MUST not change the order of these. If a new analytics tag is added it
// must be added to the end, and you must coordinate with the other platforms to make sure
// they are using the exact same position and values!
export const getAnalyticsTags = ({
  user,
  isMobile,
  searchOrigin,
  languageRegionCode = 'en-US',
  query
}: {
  user: User | null;
  isMobile?: boolean;
  searchOrigin?: SearchOrigin;
  languageRegionCode: LanguageRegionCode;
  query: string;
}) => {
  const tags = [] as string[];

  if (user) {
    if (isSubscriber(user)) {
      tags.push('auth:pro');
    } else {
      tags.push('auth:free');
    }
  } else {
    tags.push('auth:none');
  }

  if (isMobile) {
    tags.push('platform:mobileweb');
  } else {
    tags.push('platform:web');
  }

  if (searchOrigin) {
    tags.push(`origin:${searchOrigin}`);
  }

  tags.push('actor:user');
  tags.push(`lang:${getLanguageCodeFromLanguageRegionCode(languageRegionCode)}`);

  if (query && query.length > 0) {
    tags.push('hasquery:true');
  } else {
    tags.push('hasquery:false');
  }

  return tags;
};

export const getSearchSelectionType = (searchType?: SearchType) => {
  switch (searchType) {
    case 'recent':
      return 'recent';
    case 'nearby':
      return 'nearby';
    default:
      return 'search suggestion';
  }
};

export const getSearchItemType = (resultType: AlgoliaResultType) => {
  switch (resultType) {
    case 'area':
      return 'area';
    case 'attribute':
      return 'attribute';
    case 'filter':
      return 'filter';
    case 'guide':
    case 'page':
      return 'guide';
    case 'list':
      return 'list';
    case 'map':
      return 'map';
    case 'place':
      return 'place';
    case 'poi':
      return 'poi';
    case 'track':
      return 'track';
    case 'trail':
      return 'trail';
    default:
      return undefined;
  }
};
